/**
 * Combined flow chart for an eval scenario run.
 *
 * Renders the real agent pipeline nodes (reusing the shared slack flow node
 * configs / dialogs, identical to triggers and user activity) interleaved with
 * eval-specific step nodes (interact turns, assert checks, final outcome).
 */

import { Background, Controls, ReactFlow } from '@xyflow/react';
import { useMemo, useState } from 'react';

import type { ObservabilityLogsRetrieve200ResultsItem } from '@/gen-api/models';
import { AppNode } from '@/components/flow/ui/AppNode';
import { InfoDialog } from '@/components/flow/ui/InfoDialog';
import type { DialogState } from '@/components/flow/types';
import type { ScenarioRunDetail } from '@/components/eval-studio/evalStudioApi';
import { buildScenarioRunGraph } from '@/components/eval-studio/scenarioFlowSteps';
import { useEvalStudioAgentNames } from '@/components/eval-studio/useEvalStudioAgentNames';
import type { ScenarioRunSummary, ScenarioRunTrace } from '@/types/eval-studio';
import { useDarkMode } from '@/hooks/useDarkMode';

type ScenarioRunFlowProps = {
  run: ScenarioRunSummary;
  trace?: ScenarioRunTrace | null;
  detail?: ScenarioRunDetail | null;
  logsById?: Map<string, ObservabilityLogsRetrieve200ResultsItem>;
  height?: number;
};

export function ScenarioRunFlow({ run, trace, detail, logsById, height = 600 }: ScenarioRunFlowProps) {
  const [theme] = useDarkMode();
  const [dialog, setDialog] = useState<DialogState>(null);
  const agentNames = useEvalStudioAgentNames();

  const graph = useMemo(
    () => buildScenarioRunGraph(run, trace, logsById, detail, agentNames),
    [run, trace, logsById, detail, agentNames]
  );

  const nodeTypes = useMemo(() => ({ appNode: AppNode }), []);

  return (
    <div style={{ height }}>
      <ReactFlow
        nodes={graph.nodes}
        edges={graph.edges}
        nodeTypes={nodeTypes}
        colorMode={theme}
        fitView
        zoomOnScroll={false}
        onNodeClick={(_, node) => {
          const entry = graph.dialogs.get(node.id);
          if (!entry) return;
          setDialog({ open: true, title: entry.title, fields: entry.fields });
        }}
        defaultEdgeOptions={{
          type: 'smoothstep',
          animated: true,
          style: { strokeWidth: 2, strokeDasharray: '6 10', animationDuration: '2s' },
        }}
      >
        <Background />
        <Controls showInteractive={false} />
      </ReactFlow>
      <InfoDialog state={dialog} onClose={() => setDialog(null)} />
    </div>
  );
}

export default ScenarioRunFlow;
